import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { api, getUser } from '@/utils/api';
import { ArrowLeft, Clock, User, Mail, Phone, FileText, Paperclip, Ticket, ChevronDown, Shield, ExternalLink } from 'lucide-react';
import { format, differenceInDays } from 'date-fns';
import DemoBanner from '@/components/DemoBanner';
import useDemoExpiry from '@/hooks/useDemoExpiry';

const AttachmentViewer = ({ attachment }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    const loadFile = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/file/${attachment.file_id}`, { responseType: 'blob' });
        setBlobUrl(URL.createObjectURL(response.data));
      } catch {
        setError('Failed to load file');
      } finally {
        setLoading(false);
      }
    };
    loadFile();
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [attachment.file_id]);

  const handleDownload = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/file/${attachment.file_id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download file');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !blobUrl) {
    return (
      <div className="border border-border rounded-lg p-3">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="ml-2 text-sm text-muted-foreground">Loading attachment...</span>
        </div>
      </div>
    );
  }
  if (error) return <div className="border border-border rounded-lg p-3"><p className="text-sm text-destructive">{error}</p></div>;

  return (
    <div className="border border-border rounded-lg p-3">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-medium">{attachment.filename}</p>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">{attachment.size_mb} MB</span>
          <Button variant="outline" size="sm" onClick={handleDownload} disabled={loading} className="text-xs" data-testid={`download-btn-${attachment.file_id}`}>
            {loading ? 'Downloading...' : 'Download'}
          </Button>
        </div>
      </div>
      {attachment.content_type?.startsWith('image') && blobUrl ? (
        <img src={blobUrl} alt={attachment.filename} className="max-w-full h-auto rounded-lg max-h-96 object-contain border border-border" data-testid={`image-preview-${attachment.file_id}`} />
      ) : attachment.content_type?.startsWith('video') && blobUrl ? (
        <div>
          <video src={blobUrl} controls className="max-w-full h-auto rounded-lg max-h-[500px] w-full bg-black" preload="metadata" data-testid={`video-preview-${attachment.file_id}`}>
            Your browser does not support the video tag.
          </video>
          <p className="text-xs text-muted-foreground mt-2">Click play to view the video or use the Download button above</p>
        </div>
      ) : (
        <div className="bg-muted/50 p-4 rounded-lg">
          <p className="text-sm text-muted-foreground">This file type cannot be previewed. Click Download to view it.</p>
        </div>
      )}
    </div>
  );
};

// Resolution types configuration
const RESOLUTION_OPTIONS = [
  { id: 'Visit', label: 'Visit', costLabel: 'Visit Cost (₹)', costField: 'visit' },
  { id: 'Video Call', label: 'Video Call', costLabel: 'Video Call Cost (₹)', costField: 'video_call' },
  { id: 'Repair', label: 'Repair', costLabel: 'Repair Cost (₹)', costField: 'repair', hasNotes: true },
  { id: 'Replacement', label: 'Replacement', isReplacement: true, costLabel: 'Replacement Cost (₹)', costField: 'replacement' },
  { id: 'Partial Refund', label: 'Partial Refund', costLabel: 'Refund Amount (₹)', costField: 'partial_refund' },
  { id: 'Full Refund', label: 'Full Refund', costLabel: 'Refund Amount (₹)', costField: 'full_refund' },
];

const defaultResolution = {
  root_cause_analysis: { why_1: '', why_2: '', why_3: '', why_4: '', why_5: '', countermeasure: '' },
  resolution_types: [],
  costs: { visit: '', video_call: '', repair: '', repair_notes: '', partial_refund: '', full_refund: '', replacement: '' },
  replacement: { replacement_types: [], frame_model_name: '' },
  time_spent_hours: ''
};

const mergeResolution = (saved) => {
  if (!saved) return defaultResolution;

  // Handle both new format (resolution_types) and old format (solution_type)
  let resolution_types = saved.resolution_types || [];
  if (!resolution_types.length && saved.solution_type) {
    resolution_types = [saved.solution_type];
  }

  // Handle costs: new format (costs object) or old format (scattered fields)
  let costs = saved.costs ? { ...defaultResolution.costs, ...saved.costs } : { ...defaultResolution.costs };
  if (!saved.costs) {
    if (saved.repair?.repair_cost) costs.repair = saved.repair.repair_cost;
    if (saved.repair?.repair_notes) costs.repair_notes = saved.repair.repair_notes;
    if (saved.partial_refund?.amount) costs.partial_refund = saved.partial_refund.amount;
    if (saved.full_refund?.amount) costs.full_refund = saved.full_refund.amount;
    if (saved.replacement?.cost_incurred) costs.replacement = saved.replacement.cost_incurred;
  }
  // Ensure cost fields are strings for input
  Object.keys(costs).forEach(k => {
    if (k !== 'repair_notes' && costs[k] != null && costs[k] !== '') {
      costs[k] = String(costs[k]);
    }
  });

  // Handle replacement sub-object
  let replacement = { replacement_types: [], frame_model_name: '' };
  if (saved.replacement) {
    replacement.replacement_types = saved.replacement.replacement_types || [];
    if (!replacement.replacement_types.length && saved.replacement.replacement_type) {
      // Migrate old single type
      const old = saved.replacement.replacement_type;
      replacement.replacement_types = old === 'Tabletop' ? ['Top'] : old === 'Frame' ? ['Frame'] : [];
    }
    replacement.frame_model_name = saved.replacement.frame_model_name || '';
  }

  return {
    root_cause_analysis: { ...defaultResolution.root_cause_analysis, ...(saved.root_cause_analysis || {}) },
    resolution_types,
    costs,
    replacement,
    time_spent_hours: saved.time_spent_hours != null ? String(saved.time_spent_hours) : ''
  };
};

const TicketDetail = () => {
  const navigate = useNavigate();
  const { ticketId } = useParams();
  const user = getUser();
  useDemoExpiry();
  const isDemoMode = user?.role === 'demo';
  const [ticket, setTicket] = useState(null);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [isInternalNote, setIsInternalNote] = useState(true);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [warrantyIssueTypeNames, setWarrantyIssueTypeNames] = useState(new Set());
  const [damageIssueTypeNames, setDamageIssueTypeNames] = useState(new Set());
  const [resolution, setResolution] = useState(defaultResolution);
  const [resolutionOpen, setResolutionOpen] = useState(false);
  const [savingResolution, setSavingResolution] = useState(false);
  const [resolutionErrors, setResolutionErrors] = useState({});
  const [linkedWorkOrders, setLinkedWorkOrders] = useState([]);
  const [insurance, setInsurance] = useState({ raised_within_48hrs: null, not_raised_reason: '', claim_status: '' });
  const [savingInsurance, setSavingInsurance] = useState(false);

  useEffect(() => {
    if (!user || (user.role !== 'admin' && user.role !== 'demo')) { navigate('/admin/login'); return; }
    fetchData();
  }, [ticketId]);

  const fetchData = async () => {
    try {
      const [ticketRes, notesRes, teamRes, issueTypesRes] = await Promise.all([
        api.get(`/tickets/${ticketId}`),
        api.get(`/tickets/${ticketId}/notes`),
        api.get('/team-members'),
        api.get('/issue-types')
      ]);
      setTicket(ticketRes.data);
      setNotes(notesRes.data);
      setTeamMembers(teamRes.data);
      setWarrantyIssueTypeNames(new Set(issueTypesRes.data.filter(t => t.is_warranty).map(t => t.name)));
      setDamageIssueTypeNames(new Set(issueTypesRes.data.filter(t => t.is_damage).map(t => t.name)));
      setResolution(mergeResolution(ticketRes.data.resolution));

      const claim = ticketRes.data.insurance_claim || {};
      setInsurance({
        raised_within_48hrs: claim.raised_within_48hrs ?? null,
        not_raised_reason: claim.not_raised_reason || '',
        claim_status: claim.claim_status || ''
      });

      try {
        const woRes = await api.get('/work-orders', { params: { ticket_id: ticketRes.data.ticket_id } });
        setLinkedWorkOrders(woRes.data || []);
      } catch {
        setLinkedWorkOrders([]);
      }
    } catch {
      toast.error('Failed to fetch ticket details');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    try {
      await api.patch(`/tickets/${ticketId}`, { status: newStatus });
      toast.success('Status updated');
      fetchData();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || 'Failed to update status');
    }
  };

  const handleAssignmentUpdate = async (userId) => {
    try {
      await api.patch(`/tickets/${ticketId}`, { assigned_to: userId === 'unassigned' ? null : userId });
      toast.success('Ticket assigned');
      fetchData();
    } catch { toast.error('Failed to assign ticket'); }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      await api.post(`/tickets/${ticketId}/notes`, { ticket_id: ticketId, note: newNote, is_internal: isInternalNote });
      toast.success('Note added');
      setNewNote('');
      fetchData();
    } catch { toast.error('Failed to add note'); }
  };

  // Auto-calculate total cost
  const totalCost = useMemo(() => {
    const c = resolution.costs;
    return ['visit', 'video_call', 'repair', 'partial_refund', 'full_refund', 'replacement']
      .reduce((sum, k) => sum + (parseFloat(c[k]) || 0), 0);
  }, [resolution.costs]);

  const toggleResolutionType = (type) => {
    setResolution(prev => {
      const next = prev.resolution_types.includes(type)
        ? prev.resolution_types.filter(t => t !== type)
        : [...prev.resolution_types, type];
      if (next.length > 0) setResolutionErrors(e => { const n = {...e}; delete n.resolution_types; return n; });
      return { ...prev, resolution_types: next };
    });
  };

  const toggleReplacementType = (type) => {
    setResolution(prev => {
      const next = prev.replacement.replacement_types.includes(type)
        ? prev.replacement.replacement_types.filter(t => t !== type)
        : [...prev.replacement.replacement_types, type];
      if (next.length > 0) setResolutionErrors(e => { const n = {...e}; delete n.replacement_type; return n; });
      return { ...prev, replacement: { ...prev.replacement, replacement_types: next } };
    });
  };

  const setCost = (field, value) => {
    setResolution(prev => ({ ...prev, costs: { ...prev.costs, [field]: value } }));
    if (value !== '' && parseFloat(value) > 0) {
      setResolutionErrors(e => { const n = {...e}; delete n[`cost_${field}`]; return n; });
    }
    if (field === 'repair_notes' && value.trim()) {
      setResolutionErrors(e => { const n = {...e}; delete n.cost_repair_notes; return n; });
    }
  };

  const setRca = (field, value) => {
    setResolution(prev => ({ ...prev, root_cause_analysis: { ...prev.root_cause_analysis, [field]: value } }));
    if (value.trim()) setResolutionErrors(e => { const n = {...e}; delete n[field]; return n; });
  };

  const validateResolutionForm = () => {
    const errors = {};
    const rca = resolution.root_cause_analysis;
    const c = resolution.costs;

    // RCA required fields
    if (!rca.why_1?.trim()) errors.why_1 = 'Why 1 is required';
    if (!rca.why_2?.trim()) errors.why_2 = 'Why 2 is required';
    if (!rca.why_3?.trim()) errors.why_3 = 'Why 3 is required';
    if (!rca.countermeasure?.trim()) errors.countermeasure = 'Countermeasure is required';

    // At least one resolution type required
    if (!resolution.resolution_types.length) {
      errors.resolution_types = 'Please select at least one resolution type';
    }

    // Per-type validations
    resolution.resolution_types.forEach(type => {
      const opt = RESOLUTION_OPTIONS.find(o => o.id === type);

      // Cost required and > 0 for all types
      if (opt?.costField) {
        const val = c[opt.costField];
        if (val === '' || val == null || isNaN(parseFloat(val)) || parseFloat(val) <= 0) {
          errors[`cost_${opt.costField}`] = `${type} cost is required and must be greater than 0`;
        }
      }

      // Repair: notes required
      if (type === 'Repair' && !c.repair_notes?.trim()) {
        errors.cost_repair_notes = 'Repair notes are required';
      }

      // Replacement sub-validations
      if (type === 'Replacement') {
        if (!resolution.replacement.replacement_types.length) {
          errors.replacement_type = 'Select at least one replacement type (Top or Frame)';
        }
        if (resolution.replacement.replacement_types.includes('Frame') && !resolution.replacement.frame_model_name?.trim()) {
          errors.frame_model_name = 'Frame model name is required when Frame is selected';
        }
      }
    });

    return errors;
  };

  const handleSaveResolution = async () => {
    const errors = validateResolutionForm();
    if (Object.keys(errors).length > 0) {
      setResolutionErrors(errors);
      // Scroll to first error smoothly
      setTimeout(() => {
        const el = document.querySelector('[data-error="true"]');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
      return;
    }
    setResolutionErrors({});
    try {
      setSavingResolution(true);
      const toNum = (v) => (v === '' || v == null) ? null : parseFloat(v);
      const c = resolution.costs;
      const totalVal = Math.round(
        ['visit', 'video_call', 'repair', 'partial_refund', 'full_refund', 'replacement']
          .reduce((s, k) => s + (parseFloat(c[k]) || 0), 0) * 100
      ) / 100;

      const payload = {
        root_cause_analysis: {
          why_1: resolution.root_cause_analysis.why_1,
          why_2: resolution.root_cause_analysis.why_2,
          why_3: resolution.root_cause_analysis.why_3,
          why_4: resolution.root_cause_analysis.why_4 || '',
          why_5: resolution.root_cause_analysis.why_5 || '',
          countermeasure: resolution.root_cause_analysis.countermeasure
        },
        resolution_types: resolution.resolution_types,
        costs: {
          visit: toNum(c.visit),
          video_call: toNum(c.video_call),
          repair: toNum(c.repair),
          repair_notes: c.repair_notes || null,
          partial_refund: toNum(c.partial_refund),
          full_refund: toNum(c.full_refund),
          replacement: toNum(c.replacement),
          total: totalVal
        },
        replacement: resolution.resolution_types.includes('Replacement') ? {
          replacement_types: resolution.replacement.replacement_types,
          frame_model_name: resolution.replacement.frame_model_name || null
        } : null,
        time_spent_hours: toNum(resolution.time_spent_hours)
      };
      await api.patch(`/tickets/${ticketId}/resolution`, payload);
      toast.success('Resolution saved successfully');
      fetchData();
    } catch { toast.error('Failed to save resolution'); }
    finally { setSavingResolution(false); }
  };

  const handleSaveInsuranceClaim = async () => {
    try {
      setSavingInsurance(true);
      const payload = {
        raised_within_48hrs: insurance.raised_within_48hrs,
        not_raised_reason: insurance.raised_within_48hrs === false ? (insurance.not_raised_reason || null) : null,
        claim_status: insurance.raised_within_48hrs === true ? (insurance.claim_status || null) : null
      };
      await api.patch(`/tickets/${ticketId}/insurance-claim`, payload);
      toast.success('Insurance claim saved');
      fetchData();
    } catch {
      toast.error('Failed to save insurance claim');
    } finally {
      setSavingInsurance(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      'Pending': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'In-Progress': 'bg-blue-100 text-blue-800 border-blue-200',
      'Closed': 'bg-green-100 text-green-800 border-green-200'
    };
    return <Badge className={variants[status] || 'bg-gray-100 text-gray-800'}>{status}</Badge>;
  };

  const getTimeElapsed = (createdAt) => {
    const diffMs = new Date() - new Date(createdAt);
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ${diffHours % 24}h`;
    return `${diffHours} hour${diffHours !== 1 ? 's' : ''}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <nav className="border-b border-border bg-card/50 backdrop-blur-sm">
          <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
            <Button variant="ghost" onClick={() => navigate('/admin/dashboard')} data-testid="back-btn" className="h-11"><ArrowLeft className="h-4 w-4 mr-2" />Back to Dashboard</Button>
          </div>
        </nav>
        <div className="flex items-center justify-center min-h-[60vh]">Loading...</div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="min-h-screen bg-background">
        <nav className="border-b border-border bg-card/50 backdrop-blur-sm">
          <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
            <Button variant="ghost" onClick={() => navigate('/admin/dashboard')} data-testid="back-btn" className="h-11"><ArrowLeft className="h-4 w-4 mr-2" />Back to Dashboard</Button>
          </div>
        </nav>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <h2 className="text-2xl font-heading font-bold mb-2">Ticket not found</h2>
            <p className="text-muted-foreground mb-4">The ticket you're looking for doesn't exist or has been deleted.</p>
            <Button onClick={() => navigate('/admin/dashboard')}>Go to Dashboard</Button>
          </div>
        </div>
      </div>
    );
  }

  // Check warranty: issue_types collection flag, ticket-level flag (Task 5), or exact string match
  const isWarranty = warrantyIssueTypeNames.has(ticket.issue_type) || ticket.is_warranty_ticket === true ||
    ticket.issue_type?.toLowerCase().trim() === "register warranty";
  // Check damage: issue_types collection flag, ticket-level flag (Task 5), or exact string match
  const isDamageTicket = damageIssueTypeNames.has(ticket.issue_type) || ticket.is_damage_ticket === true ||
    ticket.issue_type?.toLowerCase().trim() === "received damaged item";

  // Insurance banner logic
  const savedClaim = ticket.insurance_claim || {};
  const ticketAgeHours = (new Date() - new Date(ticket.created_at)) / (1000 * 60 * 60);
  const showUrgentClaimBanner = isDamageTicket && ticketAgeHours > 48 && savedClaim.raised_within_48hrs == null;
  const showNotRaisedBanner = isDamageTicket && savedClaim.raised_within_48hrs === false;
  const lastFollowup = savedClaim.last_followup_sent ? new Date(savedClaim.last_followup_sent) : null;
  const followupOverdue = savedClaim.claim_status === 'Pending' && (!lastFollowup || (new Date() - lastFollowup) / (1000 * 60 * 60 * 24) > 2);
  const showFollowupBanner = isDamageTicket && followupOverdue;

  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b border-border bg-card/50 backdrop-blur-sm">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <Button variant="ghost" onClick={() => navigate('/admin/dashboard')} data-testid="back-btn" className="h-11"><ArrowLeft className="h-4 w-4 mr-2" />Back to Dashboard</Button>
        </div>
      </nav>
      <DemoBanner />

      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Insurance claim alert banners (damage tickets only) */}
            {showUrgentClaimBanner && (
              <div className="p-4 bg-red-50 border border-red-300 rounded-lg flex items-start gap-3" data-testid="urgent-claim-banner">
                <Shield className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                <p className="text-red-800 text-sm font-semibold">Insurance claim window — please update claim status immediately</p>
              </div>
            )}
            {showNotRaisedBanner && (
              <div className="p-4 bg-amber-50 border border-amber-300 rounded-lg flex items-start gap-3" data-testid="not-raised-banner">
                <Shield className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-amber-800 text-sm font-semibold">Insurance claim was NOT raised within 48 hours</p>
                  {savedClaim.not_raised_reason && (
                    <p className="text-amber-700 text-xs mt-1">Reason: {savedClaim.not_raised_reason}</p>
                  )}
                </div>
              </div>
            )}
            {showFollowupBanner && (
              <div className="p-4 bg-amber-50 border border-amber-300 rounded-lg flex items-start gap-3" data-testid="followup-overdue-banner">
                <Shield className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-amber-800 text-sm font-semibold">Follow-up overdue — claim still pending</p>
              </div>
            )}

            {/* Ticket Details */}
            <Card className="border-border shadow-lg">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-2xl font-heading mb-2">Ticket Details</CardTitle>
                    <p className="text-sm text-muted-foreground font-mono">ID: {ticket.ticket_id}</p>
                  </div>
                  {getStatusBadge(ticket.status)}
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div className="flex items-start gap-3">
                    <User className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div><p className="text-sm font-semibold">Customer Name</p><p className="text-sm text-muted-foreground">{ticket.first_name}</p></div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Ticket className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div><p className="text-sm font-semibold">Order ID</p><p className="text-sm text-muted-foreground">{ticket.order_id}</p></div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Mail className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div><p className="text-sm font-semibold">Email</p><p className="text-sm text-muted-foreground">{ticket.email}</p></div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Phone className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div><p className="text-sm font-semibold">Phone</p><p className="text-sm text-muted-foreground">{ticket.phone}</p></div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold">Time Elapsed</p>
                      {isWarranty ? (
                        <Badge className="bg-gray-100 text-gray-500 border border-gray-300 mt-1" data-testid="warranty-no-sla-badge">Warranty — No SLA</Badge>
                      ) : (
                        <p className="text-sm text-muted-foreground" data-testid="time-elapsed">{getTimeElapsed(ticket.created_at)}</p>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex items-start gap-3 mb-2"><FileText className="h-5 w-5 text-muted-foreground mt-0.5" /><p className="text-sm font-semibold">Issue Type</p></div>
                  <p className="text-sm text-muted-foreground ml-8">{ticket.issue_type}</p>
                </div>

                <div>
                  <p className="text-sm font-semibold mb-2">Description</p>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">{ticket.description}</p>
                </div>

                {ticket.attachments && ticket.attachments.length > 0 ? (
                  <div>
                    <div className="flex items-center gap-2 mb-3"><Paperclip className="h-5 w-5 text-muted-foreground" /><p className="text-sm font-semibold">Attachments ({ticket.attachments.length})</p></div>
                    <div className="space-y-4">{ticket.attachments.map((att, idx) => <AttachmentViewer key={idx} attachment={att} />)}</div>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center gap-2 mb-2"><Paperclip className="h-5 w-5 text-muted-foreground" /><p className="text-sm font-semibold">Attachments</p></div>
                    <div className="bg-muted/30 p-3 rounded-lg border border-border"><p className="text-sm text-muted-foreground">No attachments uploaded with this ticket</p></div>
                  </div>
                )}

                <div className="pt-4 border-t border-border">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-sm">
                    <div><p className="text-xs text-muted-foreground">Created</p><p className="font-medium">{format(new Date(ticket.created_at), 'PPpp')}</p></div>
                    {ticket.in_progress_at && <div><p className="text-xs text-muted-foreground">Started Work</p><p className="font-medium">{format(new Date(ticket.in_progress_at), 'PPpp')}</p></div>}
                    {ticket.closed_at && <div><p className="text-xs text-muted-foreground">Closed</p><p className="font-medium">{format(new Date(ticket.closed_at), 'PPpp')}</p></div>}
                    {ticket.time_to_start_hours && <div className="bg-blue-50 p-2 rounded"><p className="text-xs text-blue-600 font-semibold">Time to Start</p><p className="font-bold text-blue-900">{ticket.time_to_start_hours}h</p></div>}
                    {ticket.time_to_close_hours && <div className="bg-green-50 p-2 rounded"><p className="text-xs text-green-600 font-semibold">Time to Resolve</p><p className="font-bold text-green-900">{ticket.time_to_close_hours}h</p></div>}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Notes */}
            <Card className="border-border shadow-lg">
              <CardHeader><CardTitle className="text-xl font-heading">Notes & Updates</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {notes.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No notes yet</p>
                ) : (
                  <div className="space-y-3" data-testid="notes-list">
                    {notes.map((note) => (
                      <div key={note.note_id} className={`p-4 rounded-lg ${note.is_internal ? 'bg-amber-50 border border-amber-200' : 'bg-blue-50 border border-blue-200'}`}>
                        <div className="flex items-center gap-2 mb-2">
                          {note.is_internal ? <Badge variant="outline" className="text-xs">Internal</Badge> : <Badge className="text-xs bg-blue-600">Customer Visible</Badge>}
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{note.note}</p>
                        <p className="text-xs text-muted-foreground mt-2">{format(new Date(note.created_at), 'PPpp')}</p>
                      </div>
                    ))}
                  </div>
                )}
                <div className="pt-4 border-t border-border">
                  <Label htmlFor="new-note">Add Note</Label>
                  <Textarea id="new-note" data-testid="note-textarea" placeholder="Add note or update for customer..." value={newNote} onChange={(e) => setNewNote(e.target.value)} rows={3} className="mt-1.5" disabled={isDemoMode} title={isDemoMode ? 'Not available in demo' : undefined} />
                  <div className="flex items-center gap-2 mt-3">
                    <input type="checkbox" id="internal-note" checked={isInternalNote} onChange={(e) => setIsInternalNote(e.target.checked)} className="h-4 w-4" />
                    <Label htmlFor="internal-note" className="text-sm cursor-pointer">Internal only (not visible to customer)</Label>
                  </div>
                  <Button onClick={handleAddNote} className="mt-3 w-full sm:w-auto h-11" data-testid="add-note-btn" disabled={isDemoMode} title={isDemoMode ? 'Not available in demo' : undefined}>Add Note</Button>
                </div>
              </CardContent>
            </Card>

            {/* Resolution Details — Admin & Demo (read-only for demo) */}
            {(user?.role === 'admin' || user?.role === 'demo') && (
              <Card className="border-border shadow-lg" data-testid="resolution-section">
                <CardHeader className="cursor-pointer select-none" onClick={() => setResolutionOpen(prev => !prev)}>
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-xl font-heading">Resolution Details</CardTitle>
                    <ChevronDown className={`h-5 w-5 text-muted-foreground transition-transform duration-200 ${resolutionOpen ? 'rotate-180' : ''}`} data-testid="resolution-toggle-icon" />
                  </div>
                </CardHeader>

                {resolutionOpen && (
                  <CardContent className="space-y-8" data-testid="resolution-form">

                    {/* Root Cause Analysis */}
                    <div>
                      <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-4">Root Cause Analysis (5 Whys)</h3>
                      <div className="space-y-3">
                        {[1, 2, 3, 4, 5].map(n => (
                          <div key={n}>
                            <Label className="text-sm">
                              Why {n}{n <= 3 && <span className="text-red-500 ml-1">*</span>}
                            </Label>
                            <Textarea rows={2}
                              className={`mt-1 ${resolutionErrors[`why_${n}`] ? 'border-red-400 focus-visible:ring-red-400' : ''}`}
                              placeholder={`Why ${n}...`}
                              value={resolution.root_cause_analysis[`why_${n}`]}
                              onChange={(e) => setRca(`why_${n}`, e.target.value)}
                              data-testid={`why-${n}-textarea`}
                              data-error={!!resolutionErrors[`why_${n}`]} />
                            {resolutionErrors[`why_${n}`] && (
                              <p className="text-red-500 text-xs mt-1" data-testid={`error-why-${n}`}>{resolutionErrors[`why_${n}`]}</p>
                            )}
                          </div>
                        ))}
                        <div>
                          <Label className="text-sm">Countermeasure to Prevent Recurrence <span className="text-red-500">*</span></Label>
                          <Textarea rows={2}
                            className={`mt-1 ${resolutionErrors.countermeasure ? 'border-red-400 focus-visible:ring-red-400' : ''}`}
                            placeholder="Describe the countermeasure..."
                            value={resolution.root_cause_analysis.countermeasure}
                            onChange={(e) => setRca('countermeasure', e.target.value)}
                            data-testid="countermeasure-textarea"
                            data-error={!!resolutionErrors.countermeasure} />
                          {resolutionErrors.countermeasure && (
                            <p className="text-red-500 text-xs mt-1" data-testid="error-countermeasure">{resolutionErrors.countermeasure}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Resolution Types — multiselect checkboxes */}
                    <div>
                      <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-4">Resolution Actions</h3>
                      {resolutionErrors.resolution_types && (
                        <p className="text-red-500 text-xs mb-3 font-medium" data-testid="error-resolution-types">{resolutionErrors.resolution_types}</p>
                      )}
                      <div className="space-y-3" data-testid="resolution-types-section">
                        {RESOLUTION_OPTIONS.map(opt => {
                          const isChecked = resolution.resolution_types.includes(opt.id);
                          return (
                            <div key={opt.id}>
                              {/* Checkbox row */}
                              <label className={`flex items-center gap-3 cursor-pointer p-3 rounded-lg border transition-colors ${resolutionErrors.resolution_types ? 'border-red-300 hover:bg-red-50' : 'border-border hover:bg-muted/30'}`}
                                data-testid={`resolution-type-checkbox-${opt.id.toLowerCase().replace(/ /g, '-')}`}>
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => toggleResolutionType(opt.id)}
                                  className="h-4 w-4 accent-primary"
                                />
                                <span className="text-sm font-medium">{opt.label}</span>
                              </label>

                              {/* Expanded fields when checked */}
                              {isChecked && (
                                <div className="ml-7 mt-2 p-4 bg-muted/20 rounded-lg border border-border space-y-3"
                                  data-testid={`resolution-fields-${opt.id.toLowerCase().replace(/ /g, '-')}`}>

                                  {/* Cost field */}
                                  {opt.costLabel && (
                                    <div>
                                      <Label className="text-sm">{opt.costLabel} <span className="text-red-500">*</span></Label>
                      <Input type="number" min="0" step="0.01"
                                        className={`mt-1 w-full sm:max-w-xs ${resolutionErrors[`cost_${opt.costField}`] ? 'border-red-400 focus-visible:ring-red-400' : ''}`}
                                        placeholder="0.00"
                                        value={resolution.costs[opt.costField]}
                                        onChange={(e) => setCost(opt.costField, e.target.value)}
                                        data-testid={`cost-${opt.costField}-input`}
                                        data-error={!!resolutionErrors[`cost_${opt.costField}`]} />
                                      {resolutionErrors[`cost_${opt.costField}`] && (
                                        <p className="text-red-500 text-xs mt-1" data-testid={`error-cost-${opt.costField}`}>{resolutionErrors[`cost_${opt.costField}`]}</p>
                                      )}
                                    </div>
                                  )}

                                  {/* Repair notes */}
                                  {opt.hasNotes && (
                                    <div>
                                      <Label className="text-sm">Repair Notes <span className="text-red-500">*</span></Label>
                                      <Textarea rows={3}
                                        className={`mt-1 ${resolutionErrors.cost_repair_notes ? 'border-red-400 focus-visible:ring-red-400' : ''}`}
                                        placeholder="Describe the repair..."
                                        value={resolution.costs.repair_notes}
                                        onChange={(e) => setCost('repair_notes', e.target.value)}
                                        data-testid="repair-notes-textarea"
                                        data-error={!!resolutionErrors.cost_repair_notes} />
                                      {resolutionErrors.cost_repair_notes && (
                                        <p className="text-red-500 text-xs mt-1" data-testid="error-repair-notes">{resolutionErrors.cost_repair_notes}</p>
                                      )}
                                    </div>
                                  )}

                                  {/* Replacement sub-section */}
                                  {opt.isReplacement && (
                                    <div className="space-y-3" data-testid="replacement-subsection">
                                      <div>
                                        <Label className="text-sm font-medium">Replacement Type <span className="text-red-500">*</span></Label>
                                        <div className={`flex flex-wrap gap-4 mt-2 p-2 rounded-md ${resolutionErrors.replacement_type ? 'bg-red-50 border border-red-300' : ''}`}
                                          data-testid="replacement-type-checkboxes">
                                          {['Top', 'Frame'].map(rtype => (
                                            <label key={rtype} className="flex items-center gap-2 cursor-pointer text-sm"
                                              data-testid={`replacement-type-${rtype.toLowerCase()}`}>
                                              <input
                                                type="checkbox"
                                                checked={resolution.replacement.replacement_types.includes(rtype)}
                                                onChange={() => toggleReplacementType(rtype)}
                                                className="h-4 w-4 accent-primary"
                                              />
                                              {rtype}
                                            </label>
                                          ))}
                                        </div>
                                        {resolutionErrors.replacement_type && (
                                          <p className="text-red-500 text-xs mt-1" data-testid="error-replacement-type">{resolutionErrors.replacement_type}</p>
                                        )}
                                      </div>
                                      {resolution.replacement.replacement_types.includes('Frame') && (
                                        <div data-testid="frame-model-name-field">
                                          <Label className="text-sm">Frame Model Name <span className="text-red-500">*</span></Label>
                                          <Input
                                            className={`mt-1 max-w-xs ${resolutionErrors.frame_model_name ? 'border-red-400 focus-visible:ring-red-400' : ''}`}
                                            placeholder="Enter frame model name..."
                                            value={resolution.replacement.frame_model_name}
                                            onChange={(e) => {
                                              setResolution(prev => ({ ...prev, replacement: { ...prev.replacement, frame_model_name: e.target.value } }));
                                              if (e.target.value.trim()) setResolutionErrors(err => { const n = {...err}; delete n.frame_model_name; return n; });
                                            }}
                                            data-testid="frame-model-name-input"
                                            data-error={!!resolutionErrors.frame_model_name} />
                                          {resolutionErrors.frame_model_name && (
                                            <p className="text-red-500 text-xs mt-1" data-testid="error-frame-model-name">{resolutionErrors.frame_model_name}</p>
                                          )}
                                        </div>
                                      )}
                                      {resolution.replacement.replacement_types.length > 0 && (
                                        <p className="text-xs text-muted-foreground">
                                          Work order(s) will be auto-created:
                                          {resolution.replacement.replacement_types.includes('Top') && ' Tabletop (17 days SLA)'}
                                          {resolution.replacement.replacement_types.includes('Top') && resolution.replacement.replacement_types.includes('Frame') && ','}
                                          {resolution.replacement.replacement_types.includes('Frame') && ' Frame (14 days SLA)'}
                                        </p>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Auto-calculated total */}
                      <div className="mt-4 p-3 bg-slate-50 border border-slate-200 rounded-lg flex items-center justify-between" data-testid="total-cost-display">
                        <span className="text-sm font-semibold text-slate-700">Total Cost Incurred</span>
                        <span className="text-lg font-bold text-green-700" data-testid="total-cost-value">
                          ₹{totalCost.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    </div>

                    {/* Time Spent */}
                    <div>
                      <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">Time</h3>
                      <div>
                        <Label className="text-sm">Time Spent Resolving (hours)</Label>
                        <Input type="number" min="0" step="0.5" className="mt-1 w-full sm:max-w-xs" placeholder="0.0"
                          value={resolution.time_spent_hours}
                          onChange={(e) => setResolution(prev => ({ ...prev, time_spent_hours: e.target.value }))}
                          data-testid="time-spent-input" />
                      </div>
                    </div>

                    {/* Save */}
                    <div className="pt-4 border-t border-border">
                      <Button onClick={handleSaveResolution} disabled={savingResolution || isDemoMode} data-testid="save-resolution-btn" className="w-full sm:w-auto h-11" title={isDemoMode ? 'Not available in demo' : undefined}>
                        {savingResolution ? 'Saving...' : 'Save Resolution'}
                      </Button>
                    </div>
                  </CardContent>
                )}
              </Card>
            )}

            {/* Linked Work Orders Card */}
            {linkedWorkOrders.length > 0 && (
              <div className="space-y-3">
                {linkedWorkOrders.map((wo) => {
                  const woStagesDone = Object.values(wo.stages || {}).filter(s => s.done).length;
                  const woDaysLeft = differenceInDays(new Date(wo.sla_deadline), new Date());
                  return (
                    <Card key={wo.work_order_id} className="border-border shadow-lg" data-testid="linked-work-order-card">
                      <CardHeader className="pb-3">
                        <div className="flex justify-between items-center">
                          <CardTitle className="text-base font-heading">Replacement Work Order — {wo.replacement_type}</CardTitle>
                          <Badge className={wo.status === 'Completed' ? 'bg-green-100 text-green-800 border-green-200' : 'bg-yellow-100 text-yellow-800 border-yellow-200'}>
                            {wo.status}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-mono font-semibold">{wo.work_order_id}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {woStagesDone}/7 stages &middot; {woDaysLeft < 0 ? `${Math.abs(woDaysLeft)}d overdue` : `${woDaysLeft}d remaining`}
                            </p>
                          </div>
                          <Button variant="outline" size="sm"
                            onClick={() => navigate(`/admin/work-orders/${wo.work_order_id}`)}
                            data-testid="view-work-order-btn">
                            <ExternalLink className="h-4 w-4 mr-1.5" />
                            View Work Order
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Insurance Claim Section — Admin & Demo, Damage tickets only */}
            {(user?.role === 'admin' || user?.role === 'demo') && isDamageTicket && (
              <Card className="border-border shadow-lg" data-testid="insurance-claim-section">
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Shield className="h-5 w-5 text-muted-foreground" />
                    <CardTitle className="text-xl font-heading">Insurance Claim</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div>
                    <Label className="text-sm font-semibold">Was claim raised within 48 hours?</Label>
                    <div className="flex flex-wrap gap-4 mt-2">
                      <label className="flex items-center gap-2 cursor-pointer text-sm" data-testid="claim-raised-yes">
                        <input type="radio" name="raised_within_48hrs"
                          checked={insurance.raised_within_48hrs === true}
                          onChange={() => setInsurance(prev => ({ ...prev, raised_within_48hrs: true, not_raised_reason: '' }))}
                          className="h-4 w-4 accent-primary" />
                        Yes
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer text-sm" data-testid="claim-raised-no">
                        <input type="radio" name="raised_within_48hrs"
                          checked={insurance.raised_within_48hrs === false}
                          onChange={() => setInsurance(prev => ({ ...prev, raised_within_48hrs: false, claim_status: '' }))}
                          className="h-4 w-4 accent-primary" />
                        No
                      </label>
                    </div>
                  </div>

                  {insurance.raised_within_48hrs === false && (
                    <div data-testid="not-raised-reason-field">
                      <Label className="text-sm">Reason claim was not raised</Label>
                      <textarea rows={3}
                        className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none"
                        placeholder="Explain why the claim was not raised within 48 hours..."
                        value={insurance.not_raised_reason}
                        onChange={(e) => setInsurance(prev => ({ ...prev, not_raised_reason: e.target.value }))}
                        data-testid="not-raised-reason-textarea" />
                    </div>
                  )}

                  {insurance.raised_within_48hrs === true && (
                    <div data-testid="claim-status-field">
                      <Label className="text-sm">Claim Status</Label>
                      <Select value={insurance.claim_status || ''} onValueChange={(val) => setInsurance(prev => ({ ...prev, claim_status: val }))}>
                        <SelectTrigger className="mt-1" data-testid="claim-status-select">
                          <SelectValue placeholder="Select status..." />
                        </SelectTrigger>
                        <SelectContent>
                          {['Pending', 'Acknowledged', 'Approved', 'Rejected', 'Settled'].map(s => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {(savedClaim.raised_at || savedClaim.last_followup_sent) && (
                    <div className="pt-3 border-t border-border space-y-1" data-testid="claim-timeline">
                      {savedClaim.raised_at && (
                        <p className="text-xs text-muted-foreground">
                          Claim raised on: <span className="font-medium">{format(new Date(savedClaim.raised_at), 'PPp')}</span>
                        </p>
                      )}
                      {savedClaim.last_followup_sent && (
                        <p className="text-xs text-muted-foreground">
                          Last follow-up sent: <span className="font-medium">{format(new Date(savedClaim.last_followup_sent), 'PPp')}</span>
                        </p>
                      )}
                    </div>
                  )}

                  <div className="pt-2 border-t border-border">
                    <Button onClick={handleSaveInsuranceClaim} disabled={savingInsurance || isDemoMode} data-testid="save-insurance-btn" className="w-full sm:w-auto h-11" title={isDemoMode ? 'Not available in demo' : undefined}>
                      {savingInsurance ? 'Saving...' : 'Save Insurance Claim'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right sidebar */}
          <div className="space-y-6">
            <Card className="border-border shadow-lg">
              <CardHeader><CardTitle className="text-lg font-heading">Update Status</CardTitle></CardHeader>
              <CardContent>
                <Label htmlFor="status">Status</Label>
                <Select value={ticket.status} onValueChange={handleStatusUpdate} disabled={isDemoMode}>
                  <SelectTrigger className="mt-1.5" data-testid="status-select" title={isDemoMode ? 'Not available in demo' : undefined}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pending">Pending</SelectItem>
                    <SelectItem value="In-Progress">In-Progress</SelectItem>
                    <SelectItem value="Closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            <Card className="border-border shadow-lg">
              <CardHeader><CardTitle className="text-lg font-heading">Assign Ticket</CardTitle></CardHeader>
              <CardContent>
                <Label htmlFor="assign">Assign To</Label>
                <Select value={ticket.assigned_to || 'unassigned'} onValueChange={handleAssignmentUpdate} disabled={isDemoMode}>
                  <SelectTrigger className="mt-1.5" data-testid="assign-select" title={isDemoMode ? 'Not available in demo' : undefined}><SelectValue placeholder="Select team member" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {teamMembers.map((member) => (
                      <SelectItem key={member.user_id} value={member.user_id}>{member.first_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketDetail;
